import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSearch,
  faTrash,
  faEdit,
  faCheckCircle,
  faTimesCircle,
  faBoxes,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import API from "../../Api/Api";
import Loading from "../../layouts/Loading";

function formatMoney(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}

function Bundles() {
  const [bundles, setBundles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentBundle, setCurrentBundle] = useState(null);
  const [itemSearch, setItemSearch] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    itemIds: [],
    isActive: true,
    kind: "pack",
    pickCount: "",
    pricingMode: "fixed",
    discountPercent: "",
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [bundlesRes, catalogRes] = await Promise.all([
        API.get("/bundles/admin"),
        API.get("/bundles/catalog"),
      ]);
      setBundles(bundlesRes.data || []);
      setCatalog(catalogRes.data || []);
    } catch (error) {
      console.error("Error loading bundles:", error);
      Swal.fire("Error", "Failed to load bundles", "error");
    } finally {
      setLoading(false);
    }
  };

  const selectedItems = useMemo(
    () => catalog.filter((item) => formData.itemIds.includes(item._id)),
    [catalog, formData.itemIds]
  );

  const oldPrice = selectedItems.reduce((sum, item) => sum + Number(item.effectivePrice || 0), 0);

  const filteredCatalog = catalog.filter((item) => {
    const q = itemSearch.toLowerCase();
    return (
      item.title.toLowerCase().includes(q) ||
      String(item.category || "").toLowerCase().includes(q) ||
      String(item.state || "").toLowerCase().includes(q)
    );
  });

  const handleOpenModal = (bundle = null) => {
    if (bundle) {
      setCurrentBundle(bundle);
      setFormData({
        name: bundle.name || "",
        price: bundle.price ?? "",
        itemIds: bundle.itemIds || (bundle.items || []).map((item) => item._id),
        isActive: bundle.isActive !== false,
        kind: bundle.kind || "pack",
        pickCount: bundle.pickCount || "",
        pricingMode: bundle.pricingMode || "fixed",
        discountPercent: bundle.discountPercent || "",
      });
    } else {
      setCurrentBundle(null);
      setFormData({
        name: "",
        price: "",
        itemIds: [],
        isActive: true,
        kind: "pack",
        pickCount: "",
        pricingMode: "fixed",
        discountPercent: "",
      });
    }
    setItemSearch("");
    setIsModalOpen(true);
  };

  const toggleItem = (id) => {
    setFormData((prev) => ({
      ...prev,
      itemIds: prev.itemIds.includes(id)
        ? prev.itemIds.filter((itemId) => itemId !== id)
        : [...prev.itemIds, id],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name.trim(),
        price: Number(formData.price || 0),
        itemIds: formData.itemIds,
        isActive: formData.isActive,
        kind: formData.kind,
        pickCount: Number(formData.pickCount || 0),
        pricingMode: formData.pricingMode,
        discountPercent: Number(formData.discountPercent || 0),
      };

      if (currentBundle) {
        await API.put(`/bundles/${currentBundle._id}`, payload);
        Swal.fire("Success", "Bundle updated successfully", "success");
      } else {
        await API.post("/bundles", payload);
        Swal.fire("Success", "Bundle created successfully", "success");
      }
      setIsModalOpen(false);
      fetchAll();
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to save bundle";
      Swal.fire("Error", msg, "error");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete this bundle?",
      text: "Customers will no longer see it on the storefront.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
    });

    if (!result.isConfirmed) return;

    try {
      await API.delete(`/bundles/${id}`);
      Swal.fire("Deleted", "Bundle has been deleted.", "success");
      fetchAll();
    } catch {
      Swal.fire("Error", "Failed to delete bundle", "error");
    }
  };

  const handleToggleActive = async (bundle) => {
    try {
      await API.put(`/bundles/${bundle._id}`, { isActive: !bundle.isActive });
      fetchAll();
    } catch {
      Swal.fire("Error", "Failed to update bundle status", "error");
    }
  };

  const filteredBundles = bundles.filter((bundle) =>
    String(bundle.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const content = (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FontAwesomeIcon icon={faBoxes} className="text-yellow-500" />
            Bundles
          </h1>
          <p className="text-gray-400 mt-1">
            Create premium packs, pick the included mods, and set a bundle price.
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-yellow-500 hover:bg-yellow-400 text-black px-6 py-2.5 rounded-lg font-semibold transition-all flex items-center gap-2 shadow-lg shadow-yellow-900/30"
        >
          <FontAwesomeIcon icon={faPlus} />
          Create Bundle
        </button>
      </div>

      <div className="bg-gray-800 rounded-xl shadow-xl overflow-hidden border border-gray-700">
        <div className="p-4 border-b border-gray-700 bg-gray-800/50 flex items-center">
          <div className="relative w-full max-w-md">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search bundles..."
              className="w-full bg-gray-900 border border-gray-700 text-white pl-10 pr-4 py-2 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none transition-all"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-20 flex justify-center">
              <Loading />
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-gray-900/50 text-gray-400 text-sm uppercase">
                <tr>
                  <th className="px-6 py-4">Bundle</th>
                  <th className="px-6 py-4">Type</th>
                  <th className="px-6 py-4">Items</th>
                  <th className="px-6 py-4">Catalog total</th>
                  <th className="px-6 py-4">Offer</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredBundles.length > 0 ? (
                  filteredBundles.map((bundle) => (
                    <tr key={bundle._id} className="hover:bg-gray-700/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="text-white font-semibold">{bundle.name}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {bundle.kind === "build"
                            ? `Pick ${bundle.pickCount} of ${(bundle.items || []).length}`
                            : `${(bundle.items || []).length} included`}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">
                        {bundle.kind === "build" ? "Build a bundle" : "Full pack"}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2 max-w-md">
                          {(bundle.items || []).slice(0, 6).map((item) => (
                            <span
                              key={item._id}
                              className="bg-gray-900 text-gray-300 text-xs px-2 py-1 rounded border border-gray-700"
                            >
                              {item.title}
                            </span>
                          ))}
                          {(bundle.items || []).length > 6 && (
                            <span className="text-xs text-gray-500">
                              +{bundle.items.length - 6} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-400 line-through">
                        ${formatMoney(bundle.oldPrice)}
                      </td>
                      <td className="px-6 py-4 text-yellow-400 font-bold">
                        {bundle.pricingMode === "percent"
                          ? `${bundle.discountPercent}% off`
                          : `$${formatMoney(bundle.price)}`}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          onClick={() => handleToggleActive(bundle)}
                          className="text-sm"
                        >
                          {bundle.isActive ? (
                            <span className="flex items-center gap-1.5 text-green-400">
                              <FontAwesomeIcon icon={faCheckCircle} /> Active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 text-gray-500">
                              <FontAwesomeIcon icon={faTimesCircle} /> Hidden
                            </span>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2 text-lg">
                          <button
                            onClick={() => handleOpenModal(bundle)}
                            className="p-1.5 text-yellow-400 hover:bg-yellow-400/10 rounded transition-all"
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            onClick={() => handleDelete(bundle._id)}
                            className="p-1.5 text-red-400 hover:bg-red-400/10 rounded transition-all"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-20 text-center text-gray-500">
                      No bundles yet. Click Create Bundle to add one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-800 w-full max-w-3xl max-h-[92vh] rounded-2xl shadow-2xl border border-gray-700 overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center bg-gray-900/30">
              <h2 className="text-xl font-bold text-white">
                {currentBundle ? "Edit Bundle" : "Create Bundle"}
              </h2>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-white text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-5 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Bundle name</label>
                <input
                  type="text"
                  required
                  className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Starter Creator Pack"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Bundle type</label>
                  <select
                    className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none"
                    value={formData.kind}
                    onChange={(e) => setFormData({ ...formData, kind: e.target.value })}
                  >
                    <option value="pack">Give all selected items</option>
                    <option value="build">Customer picks some of them</option>
                  </select>
                </div>
                {formData.kind === "build" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      How many can they choose
                    </label>
                    <input
                      type="number"
                      min="1"
                      max={Math.max(1, formData.itemIds.length)}
                      className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none"
                      value={formData.pickCount}
                      onChange={(e) => setFormData({ ...formData, pickCount: e.target.value })}
                      placeholder="e.g. 3"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">End price type</label>
                  <select
                    className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none"
                    value={formData.pricingMode}
                    onChange={(e) => setFormData({ ...formData, pricingMode: e.target.value })}
                  >
                    <option value="fixed">Fixed amount</option>
                    <option value="percent">Discount percentage</option>
                  </select>
                </div>
                {formData.pricingMode === "percent" ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Discount %</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      step="0.01"
                      className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none"
                      value={formData.discountPercent}
                      onChange={(e) => setFormData({ ...formData, discountPercent: e.target.value })}
                      placeholder="e.g. 30"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Bundle price ($)</label>
                    <input
                      type="number"
                      required={formData.pricingMode === "fixed"}
                      min="0"
                      step="0.01"
                      className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="29.99"
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-yellow-700/40 bg-yellow-500/5 px-4 py-3">
                <div className="text-sm text-gray-300">
                  Selected items: <span className="text-white font-semibold">{formData.itemIds.length}</span>
                  {formData.kind === "build" && formData.pickCount && (
                    <span className="text-yellow-400"> · pick any {formData.pickCount}</span>
                  )}
                </div>
                <div className="text-sm">
                  <span className="text-gray-500 line-through mr-3">${formatMoney(oldPrice)}</span>
                  <span className="text-yellow-400 font-bold">
                    {formData.pricingMode === "percent"
                      ? `${formData.discountPercent || 0}% off`
                      : `New price $${formatMoney(formData.price || 0)}`}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Select bundle items</label>
                <div className="relative mb-3">
                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search mods, maps, models..."
                    className="w-full bg-gray-900 border border-gray-700 text-white pl-10 pr-4 py-2 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none"
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                  />
                </div>
                <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-700 divide-y divide-gray-800">
                  {filteredCatalog.length ? (
                    filteredCatalog.map((item) => {
                      const checked = formData.itemIds.includes(item._id);
                      return (
                        <label
                          key={item._id}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${
                            checked ? "bg-yellow-500/10" : "hover:bg-gray-900/70"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleItem(item._id)}
                            className="w-4 h-4 accent-yellow-500"
                          />
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt=""
                              className="w-12 h-12 rounded object-cover border border-gray-700"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded bg-gray-900 border border-gray-700" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-white text-sm font-medium truncate">{item.title}</div>
                            <div className="text-xs text-gray-500">
                              {item.category || item.state || "Item"} · ${formatMoney(item.effectivePrice)}
                            </div>
                          </div>
                        </label>
                      );
                    })
                  ) : (
                    <div className="px-4 py-10 text-center text-gray-500 text-sm">No matching items.</div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="bundleActive"
                  className="w-5 h-5 rounded border-gray-700 bg-gray-900 text-yellow-500"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <label htmlFor="bundleActive" className="text-sm font-medium text-gray-300">
                  Show this bundle on the storefront
                </label>
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
                >
                  {currentBundle ? "Update Bundle" : "Create Bundle"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  return <AdminLayout Content={content} />;
}

export default Bundles;
